import Handlebars from 'handlebars';
import { api } from './api';

// In-memory template cache (session lifetime)
const templateCache = new Map();

/**
 * Register custom Handlebars helpers used by existing templates.
 * Templates like thinking-process.html use {{#if_done}}, {{#if_active}}, etc.
 * These are section helpers that check a status field.
 */
Handlebars.registerHelper('if_done', function(options) {
  return this.status === 'done' ? options.fn(this) : options.inverse(this);
});
Handlebars.registerHelper('if_active', function(options) {
  return this.status === 'active' ? options.fn(this) : options.inverse(this);
});
Handlebars.registerHelper('if_skipped', function(options) {
  return this.status === 'skipped' ? options.fn(this) : options.inverse(this);
});
Handlebars.registerHelper('if_pending', function(options) {
  return this.status === 'pending' ? options.fn(this) : options.inverse(this);
});
// Percentage helper for progress bars
Handlebars.registerHelper('percent', function(value) {
  const num = parseFloat(value);
  return isNaN(num) ? '0' : Math.round(num).toString();
});

/**
 * Fetch and cache a template's HTML source.
 * Returns compiled Handlebars template function, or null if the runtime
 * doesn't have this template registered (404). Distinguishing "not registered"
 * from "render failure" matters for the UI fallback path.
 */
export async function getCompiledTemplate(templateId) {
  if (templateCache.has(templateId)) {
    return templateCache.get(templateId);
  }

  const html = await api.getTemplateHtml(templateId);
  if (!html) {
    templateCache.set(templateId, null);
    return null;
  }

  const compiled = Handlebars.compile(html);
  templateCache.set(templateId, compiled);
  return compiled;
}

// Google's `encrypted-tbn*.gstatic.com/images?q=tbn:...` thumbnails are
// hotlink-protected — any third-party origin gets a 1×1 transparent GIF + 404.
// SerpAPI Google Shopping leaks these into product results' thumbnail field,
// which then ends up in card image_url slots. Strip them so templates fall
// back to their imageless layout instead of rendering an invisible 64×64 box.
const DEAD_IMAGE_URL_RE = /^https?:\/\/(?:encrypted-)?tbn\d*\.gstatic\.com\//i;

function scrubDeadImageUrls(value) {
  if (value == null) return value;
  if (typeof value === 'string') {
    return DEAD_IMAGE_URL_RE.test(value) ? '' : value;
  }
  if (Array.isArray(value)) {
    return value.map(scrubDeadImageUrls);
  }
  if (typeof value === 'object') {
    const out = {};
    for (const k of Object.keys(value)) {
      out[k] = scrubDeadImageUrls(value[k]);
    }
    return out;
  }
  return value;
}

/**
 * Render a template with slot data.
 *
 * Returns one of:
 *   { status: 'ready',       html: '<...>' }   — fetched + compiled + rendered
 *   { status: 'unavailable'                 }  — runtime has no such template (404)
 *   { status: 'error',       message: '...' } — render exception
 */
export async function renderTemplate(templateId, data) {
  const compiled = await getCompiledTemplate(templateId);
  if (!compiled) {
    return { status: 'unavailable' };
  }
  try {
    const safeData = scrubDeadImageUrls(data || {});
    return { status: 'ready', html: compiled(safeData) };
  } catch (err) {
    console.error(`[templateRenderer] Failed to render ${templateId}:`, err);
    return { status: 'error', message: String(err?.message || err) };
  }
}

/**
 * Clear the template cache (e.g., on session change).
 */
export function clearTemplateCache() {
  templateCache.clear();
}

/**
 * Extract title from card data for CanvasCard header.
 * Replaces extractModuleTitle from ModuleRenderer.jsx.
 */
export function extractCardTitle(templateId, data) {
  if (!data) return templateId;
  if (data.title) return data.title;
  if (data.name) return data.name;
  if (data.location) return data.location;
  if (data.food_name) return data.food_name;

  // Fallback: humanize the template ID
  const names = {
    'hero-image': 'Hero',
    'image-analysis': 'Analysis',
    'comparison-table': 'Comparison',
    'calendar-event': 'Calendar',
    'thinking-process': 'Thinking',
    'info-card': 'Info',
    'session-summary': 'Summary',
    'plant-animal-id': 'Identification',
    'step-guide': 'Guide',
    'text-result': 'Result',
    'file-download': 'Download',
    'document-scan': 'Document Scan',
    'ai-generation': 'Generated',
    'video-player': 'Video',
    'evolution-suggestion': 'Evolution',
    'memory-update': 'Memory Updated',
    'place-card': 'Place',
    'shopping-list': 'Shopping List',
    'nutrition-card': 'Nutrition',
    'checklist': 'Checklist',
    'recipe': 'Recipe',
    'weather': 'Weather',
    'quiz': 'Quiz',
    'map-pins': 'Locations',
    'image-gallery': 'Gallery',
    'conversation': 'Conversation',
    'skeleton': 'Loading',
    'agent-speech': 'VI',
    'md-list': 'List',
    'md-table': 'Table',
    'md-code': 'Code',
    'md-quote': 'Quote',
  };
  return names[templateId] || templateId;
}
